import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pulsesync-api.bcntr.com';

/**
 * Get Firebase ID token for API authentication
 * @returns {Promise<string>} - Firebase ID token
 */
const getIdToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }
  return await user.getIdToken();
};

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - API response
 */
const apiRequest = async (endpoint, options = {}) => {
  console.log('🔐 stripeService: apiRequest() called for endpoint:', endpoint);
  
  const token = await getIdToken();
  console.log('🎫 stripeService: Got Firebase token:', token ? 'Present' : 'Missing');
  
  const url = `${API_BASE_URL}${endpoint}`;
  console.log('🌐 stripeService: Full URL:', url);
  
  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };
  
  console.log('📤 stripeService: Request options:', requestOptions);
  
  const response = await fetch(url, requestOptions);
  
  console.log('📥 stripeService: Response received:', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    headers: Object.fromEntries(response.headers.entries())
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('❌ stripeService: API error response:', errorData);
    console.error('❌ stripeService: Full error details:', JSON.stringify(errorData, null, 2));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  console.log('✅ stripeService: API request successful');
  return response;
};

/**
 * Create a Stripe checkout session for subscription
 * @param {string} priceId - Stripe price ID for the subscription
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment is cancelled
 * @returns {Promise<string>} - Checkout session URL
 */
export const createCheckoutSession = async (priceId, successUrl, cancelUrl) => {
  try {
    const response = await apiRequest('/api/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({
        priceId,
        successUrl,
        cancelUrl,
      }),
    });

    const data = await response.json();
    return data.checkoutUrl;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create a customer portal session
 * @param {string} returnUrl - URL to return to after portal session
 * @returns {Promise<string>} - Portal session URL
 */
export const createPortalSession = async (returnUrl) => {
  try {
    const response = await apiRequest('/api/stripe/create-portal-session', {
      method: 'POST',
      body: JSON.stringify({
        returnUrl,
      }),
    });

    const data = await response.json();
    return data.portalUrl;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

/**
 * Get user subscription data from Firestore
 * @returns {Promise<Object|null>} - User data with subscription info
 */
export const getUserData = async () => {
  try {
    const response = await apiRequest('/api/user/data');
    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

/**
 * Check if user has an active subscription
 * @returns {Promise<boolean>} - True if user has active subscription
 */
export const hasActiveSubscription = async () => {
  try {
    const userData = await getUserData();
    return userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing';
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

/**
 * Get available products and prices for display
 * @returns {Promise<Array>} - Array of products with prices
 */
export const getProducts = async () => {
  try {
    console.log('🔍 stripeService: getProducts() called');
    console.log('🌐 stripeService: API_BASE_URL:', API_BASE_URL);
    console.log('📡 stripeService: Making request to:', `${API_BASE_URL}/api/stripe/products`);
    
    const response = await apiRequest('/api/stripe/products');
    console.log('📥 stripeService: Response received:', response);
    console.log('📊 stripeService: Response status:', response.status);
    console.log('📊 stripeService: Response ok:', response.ok);
    
    const data = await response.json();
    console.log('📋 stripeService: Response data:', data);
    console.log('📦 stripeService: Returning products:', data.products);
    
    return data;
  } catch (error) {
    console.error('❌ stripeService: Error getting products:', error);
    console.error('❌ stripeService: Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw error;
  }
};
